import React, {
  createContext,
  useContext,
  useReducer,
  useMemo,
  useEffect,
} from 'react'

export const Rfi3Context = createContext(null)

const createInitialState = ({
  supplierId,
  locationId,
  manufacturingTypeId,
  targetTab,
  commentsTab,
}) => ({
  // Query params / ids
  supplierId: supplierId || null,
  locationId: locationId || null,
  manufacturingTypeId: manufacturingTypeId || null,
  targetTab: targetTab || null,
  commentsTab: commentsTab || null,

  // Data
  rfi3Template: null,
  rfi3Data: null,
  rfiMasterData: null,
  isReadonly: false,

  // Navigation
  activeTab: { index: -1, uid: '', name: '' },
  activeRFI3Section: { index: -1, uid: '', name: '' },

  // Fetch state
  isRfi3DataFetchProgress: true,
  isRfi3DataFetchFailed: false,

  // Save state
  isRfi3SaveInProgress: false,
  isRfi3SaveFailed: false,
  isShowOverlay: false,

  // Popups
  isCommentsOpen: false,
  isAuditTrailOpen: false,

  // FastTrack
  isFastTrackInProgress: false,
  isFastTrackFailed: false,
  isFastTrackAvailable: false,
  isShowBypass: false,

  // RFI Action
  userAction: null,
  userComments: '',
  isShowEmailDialog: false,
  isShowConfirmation: false,
  isRfiActionInProgress: false,
  isRfiActionFailed: false,
  refreshRFI3Screen: false,

  // Admin
  isRfi3ActionAdminEnabled: true,

  // Capability popup
  productCapabilityDialogSettings: {
    isVisible: false,
    isReadOnly: true,
  },
  isRfiNextActionPCInProgress: false,
  isRfiNextActionPCFailed: false,

  // Validation errors
  errorCache: {},

  capabilityAudit: {
    isVisible: false,
    isReadOnly: true,
  },
  auditData: [],
  capabilityOverview: {},
})

const rfi3Reducer = (state, action) => {
  switch (action.type) {
    case 'SET_TEMPLATE':
      return { ...state, rfi3Template: action.payload }

    case 'SET_DATA':
      return { ...state, rfi3Data: action.payload }

    case 'SET_MASTER_DATA':
      return { ...state, rfiMasterData: action.payload }

    case 'SET_READONLY':
      return { ...state, isReadonly: action.payload }

    case 'FETCH_START':
      return {
        ...state,
        isRfi3DataFetchProgress: true,
        isRfi3DataFetchFailed: false,
      }

    case 'FETCH_SUCCESS':
      return {
        ...state,
        isRfi3DataFetchProgress: false,
        isRfi3DataFetchFailed: false,
      }

    case 'FETCH_FAILURE':
      return {
        ...state,
        isRfi3DataFetchProgress: false,
        isRfi3DataFetchFailed: true,
      }

    case 'SAVE_START':
      return {
        ...state,
        isRfi3SaveInProgress: true,
        isRfi3SaveFailed: false,
      }

    case 'SAVE_SUCCESS':
      return {
        ...state,
        isRfi3SaveInProgress: false,
        isRfi3SaveFailed: false,
        rfi3Data: action.payload,
      }

    case 'SAVE_FAILURE':
      return {
        ...state,
        isRfi3SaveInProgress: false,
        isRfi3SaveFailed: true,
      }

    case 'SET_OVERLAY':
      return { ...state, isShowOverlay: action.payload }

    case 'SET_ACTIVE_TAB':
      return { ...state, activeTab: action.payload }

    case 'SET_ACTIVE_SECTION':
      return { ...state, activeRFI3Section: action.payload }

    case 'SET_ERROR_CACHE':
      return { ...state, errorCache: action.payload || {} }

    case 'SET_COMMENTS_OPEN':
      return { ...state, isCommentsOpen: action.payload }

    case 'SET_AUDIT_OPEN':
      return { ...state, isAuditTrailOpen: action.payload }

    case 'SET_FAST_TRACK_IN_PROGRESS':
      return { ...state, isFastTrackInProgress: action.payload }

    case 'SET_FAST_TRACK_FAILED':
      return { ...state, isFastTrackFailed: action.payload }

    case 'SET_FAST_TRACK_AVAILABLE':
      return { ...state, isFastTrackAvailable: action.payload }

    case 'SET_SHOW_BYPASS':
      return { ...state, isShowBypass: action.payload }

    case 'SET_USER_ACTION':
      return { ...state, userAction: action.payload }

    case 'SET_USER_COMMENTS':
      return { ...state, userComments: action.payload }

    case 'SET_SHOW_EMAIL_DIALOG':
      return { ...state, isShowEmailDialog: action.payload }

    case 'SET_SHOW_CONFIRMATION':
      return { ...state, isShowConfirmation: action.payload }

    case 'SET_RFI_ACTION_IN_PROGRESS':
      return { ...state, isRfiActionInProgress: action.payload }

    case 'SET_RFI_ACTION_FAILED':
      return { ...state, isRfiActionFailed: action.payload }

    case 'SET_REFRESH_SCREEN':
      return { ...state, refreshRFI3Screen: action.payload }

    case 'SET_RFI3_ACTION_ADMIN_ENABLED':
      return { ...state, isRfi3ActionAdminEnabled: action.payload }

    case 'SET_PRODUCT_CAPABILITY_DIALOG_SETTINGS':
      return { ...state, productCapabilityDialogSettings: action.payload }

    case 'SET_CAPABILITY_AUDIT_SETTINGS':
      return { ...state, capabilityAudit: action.payload }

    case 'SET_RFI_NEXT_ACTION_PC_IN_PROGRESS':
      return { ...state, isRfiNextActionPCInProgress: action.payload }

    case 'SET_RFI_NEXT_ACTION_PC_FAILED':
      return { ...state, isRfiNextActionPCFailed: action.payload }

    case 'SET_AUDIT_DATA':
      return { ...state, auditData: action.payload }

    case 'SET_CAPABILITY_OVERVIEW': {
      return { ...state, capabilityOverview: action.payload }
    }

    case 'SET_QUERY_PARAMS':
      return {
        ...state,
        supplierId: action.payload?.supplierId ?? null,
        locationId: action.payload?.locationId ?? null,
        manufacturingTypeId: action.payload?.manufacturingTypeId ?? null,
        targetTab: action.payload?.targetTab ?? null,
        commentsTab: action.payload?.commentsTab ?? null,
      }

    default:
      return state
  }
}

export const Rfi3Provider = ({
  children,
  supplierId,
  locationId,
  manufacturingTypeId,
  targetTab,
  commentsTab,
  userSettings,
  auth,
  env,
  sendEvent,
  navigate,
}) => {
  const [state, dispatch] = useReducer(
    rfi3Reducer,
    {
      supplierId,
      locationId,
      manufacturingTypeId,
      targetTab,
      commentsTab,
    },
    createInitialState,
  )

  useEffect(() => {
    dispatch({
      type: 'SET_QUERY_PARAMS',
      payload: {
        supplierId,
        locationId,
        manufacturingTypeId,
        targetTab,
        commentsTab,
      },
    })
  }, [supplierId, locationId, manufacturingTypeId, targetTab, commentsTab])

  const contextValue = useMemo(
    () => ({
      state,
      dispatch,
      deps: {
        auth,
        env,
        sendEvent,
        navigate,
        userSettings,
      },
    }),
    [state, auth, env, sendEvent, navigate, userSettings],
  )

  return (
    <Rfi3Context.Provider value={contextValue}>{children}</Rfi3Context.Provider>
  )
}

export const useRfi3Context = () => {
  const ctx = useContext(Rfi3Context)
  if (!ctx) {
    throw new Error('useRfi3Context must be used within a Rfi3Provider')
  }
  return ctx
}
